import React from 'react';
import { FileText, CheckCircle, Loader, Workflow, Clock, DollarSign, Code, Zap, Copy, Database, Table, Edit3, ArrowRight, ArrowDownCircle, ArrowUpCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Rocket, Check, X, Sparkles } from 'lucide-react';
import FlowVisualization from './FlowVisualization';
import DataFlowVisualization from './DataFlowVisualization';

const WorkflowView = ({
  isDarkMode, currentView, workflowResult, agentDefinition, agentId,
  currentFlowIndex, currentNodeIndex, descriptionExpanded, flowExpanded,
  detailsExpanded, activeDetailTab, isEditingFlow, editedNodes, editingNodeIndex,
  nodeValidations, isValidatingNode, draftNodeValues, isSelectingFlow,
  allEditedNodesValidated, flowPillStyle, detailPillStyle,
  flowTabContainerRef, flowTabRefs, detailTabContainerRef, detailTabRefs, isAutoPilot,
  // Callbacks
  setCurrentFlowIndex, setCurrentNodeIndex, setDescriptionExpanded, setFlowExpanded,
  setDetailsExpanded, setActiveDetailTab, setEditingNodeIndex, setDraftNodeValues,
  handleEditFlow, handleCancelEdit, handleNodeEdit, handleApplySuggestion,
  handleAddNode, handleRemoveNode, handleValidateNode, handleFinalizeEdits,
  handleProceedToData, runAutoPilot, addMessage,
}) => {

  const getAvailableDetailTabs = () => {
    const tabs = [
      { id: 'node-details', label: 'Node Details', icon: FileText, available: true }
    ];

    // Prompts tab available after prompt generation or variable mapping
    if (currentView === 'prompts' || currentView === 'present' || currentView === 'execute' || currentView === 'prototype') {
      tabs.push({ id: 'prompts', label: 'Prompts', icon: Code, available: true });
    } else {
      tabs.push({ id: 'prompts', label: 'Prompts', icon: Code, available: false });
    }

    // Input/Output tab available during execution
    if (currentView === 'present' || currentView === 'execute' || currentView === 'prototype') {
      tabs.push({ id: 'io', label: 'Input/Output', icon: ArrowRight, available: true });
    } else {
      tabs.push({ id: 'io', label: 'Input/Output', icon: ArrowRight, available: false });
    }

    return tabs;
  };

  // Renders the tabbed details panel (Node Details, Data, Prompts, Input/Output)
  const renderDetailsTabs = (workflow) => {
    if (!workflow || workflow.length === 0) return null;

    const tabs = getAvailableDetailTabs();
    const currentStep = workflow[currentNodeIndex];

    return (
      <div className="space-y-4">
        {/* Tab Navigation with sliding pill */}
        <div
          ref={detailTabContainerRef}
          className={`relative flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
        >
          {/* Animated sliding pill */}
          <div
            className="absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-out"
            style={{
              left: detailPillStyle.left,
              width: detailPillStyle.width,
              opacity: detailPillStyle.opacity,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(0, 119, 168, 0.5), rgba(0, 153, 204, 0.4))'
                : 'linear-gradient(135deg, rgba(0, 119, 168, 0.25), rgba(0, 153, 204, 0.2))',
              boxShadow: '0 0 15px rgba(0, 119, 168, 0.3)',
            }}
          />
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeDetailTab === tab.id;
            const isDisabled = !tab.available;

            return (
              <button
                key={tab.id}
                ref={el => detailTabRefs.current[tab.id] = el}
                onClick={() => tab.available && setActiveDetailTab(tab.id)}
                disabled={isDisabled}
                className={`flex-1 relative z-10 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? isDarkMode
                      ? 'text-white'
                      : 'text-[#0077A8]'
                    : isDisabled
                      ? isDarkMode
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 cursor-not-allowed'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {isDisabled && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Node Navigation (shared across tabs) */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentNodeIndex(prev => Math.max(0, prev - 1))}
            disabled={currentNodeIndex === 0}
            className={`p-2 rounded-full transition-colors ${
              currentNodeIndex === 0
                ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Dot indicators */}
          <div className="flex space-x-2">
            {workflow.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentNodeIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentNodeIndex ? 'bg-[#0077A8]' : isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrentNodeIndex(prev => Math.min(workflow.length - 1, prev + 1))}
            disabled={currentNodeIndex === workflow.length - 1}
            className={`p-2 rounded-full transition-colors ${
              currentNodeIndex === workflow.length - 1
                ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300">
          {activeDetailTab === 'node-details' && (
            renderWorkflowNodeDetail(currentStep, currentNodeIndex, workflow.length)
          )}
          {activeDetailTab === 'data' && (
            <div className={`rounded-lg ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'}`}>
              <DataFlowVisualization
                nodes={workflow.map(step => ({
                  nodeId: step.node?.nodeId || step.position?.toString(),
                  alias: step.node?.display_name || step.node?.alias || `Node ${step.position}`,
                  description: step.node?.description || step.node?.business_function || ''
                }))}
                workflowSteps={workflow}
                isDarkMode={isDarkMode}
                agentDefinition={agentDefinition}
                currentNodeIndex={currentNodeIndex}
                onNodeClick={setCurrentNodeIndex}
              />
            </div>
          )}
          {activeDetailTab === 'prompts' && (
            <div className={`p-6 text-center rounded-lg ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <Code className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Prompts for node "{currentStep?.node?.display_name}" will appear here after prompt generation.
              </p>
            </div>
          )}
          {activeDetailTab === 'io' && (
            <div className={`p-6 text-center rounded-lg ${isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <ArrowRight className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Input/Output for node "{currentStep?.node?.display_name}" will appear here during execution.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowNodeDetail = (step, index, totalNodes) => {
    const { node, estimated_cost, estimated_time } = step;
    const nodeDetails = node.node_details || {};
    const hasAgentInput = nodeDetails.requires_agent_input;
    const hasIntegration = nodeDetails.requires_integration;
    const hasAgentOutput = nodeDetails.requires_agent_output;
    const agentInputHint = nodeDetails.agent_input_hint;
    const integrationHint = nodeDetails.integration_hint;
    const agentOutputHint = nodeDetails.agent_output_hint;

    // Find matching node from agent definition (if available) to get generated schemas
    const agentNode = agentDefinition?.nodes?.find(n => n.position === step.position);
    const hasGeneratedSchemas = agentNode && (agentNode.node_input || agentNode.node_output);

    // Helper to render a schema object
    const renderSchema = (schema, title, colorClass) => {
      if (!schema || typeof schema !== 'object' || Object.keys(schema).length === 0) return null;
      return (
        <div key={`schema-${title}`} className={`p-3 rounded-lg ${isDarkMode ? `${colorClass.darkBg} ${colorClass.darkBorder}` : `${colorClass.lightBg} ${colorClass.lightBorder}`}`}>
          <div className="flex items-center space-x-2 mb-2">
            <Table className={`w-4 h-4 ${colorClass.icon}`} />
            <span className={`text-sm font-semibold ${colorClass.icon}`}>{title}</span>
          </div>
          <div className="space-y-1">
            {Object.entries(schema).map(([fieldName, fieldType]) => (
              <div key={`${title}-${fieldName}`} className={`flex items-center justify-between text-xs rounded px-2 py-1.5 ${isDarkMode ? 'bg-gray-900/50' : 'bg-white/50'}`}>
                <span className={`font-mono font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{fieldName}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>{String(fieldType)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div key={index} className={`backdrop-blur-sm rounded-lg overflow-hidden shadow-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
        {/* Node Header */}
        <div className={`px-4 py-3 flex items-center justify-between ${
          hasAgentInput
            ? isDarkMode ? 'bg-cyan-900/40 border-b border-cyan-700' : 'bg-cyan-100 border-b border-cyan-300'
            : hasIntegration
              ? isDarkMode ? 'bg-rose-900/40 border-b border-rose-700' : 'bg-rose-100 border-b border-rose-300'
              : hasAgentOutput
                ? isDarkMode ? 'bg-green-900/40 border-b border-green-700' : 'bg-green-100 border-b border-green-300'
                : isDarkMode ? 'bg-blue-900/40 border-b border-blue-700' : 'bg-blue-100 border-b border-blue-300'
        }`}>
          <div className="flex items-center space-x-3">
            <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center">
              {step.position}
            </span>
            <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{node.display_name}</h4>
            {node.type === 'promptdel' && (
              <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center space-x-1 ${isDarkMode ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' : 'bg-blue-500/30 text-blue-600 border-blue-400/50'}`}>
                <Zap className="w-3 h-3" />
                <span>AI Node</span>
              </span>
            )}
          </div>
          <div className={`flex items-center space-x-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <span className="flex items-center space-x-1">
              <DollarSign className="w-3 h-3" />
              <span>${estimated_cost}</span>
            </span>
            <span className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{estimated_time}</span>
            </span>
          </div>
        </div>

        {/* Node Body */}
        <div className="p-4 space-y-3">
          <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{node.description}</p>
          <p className={`text-xs italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{node.business_function}</p>

          {/* Data Flow Info */}
          <div className="flex items-center space-x-4 text-xs">
            {nodeDetails.receives_from && (
              <div className={`flex items-center space-x-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                <ArrowRight className="w-3 h-3 rotate-180" />
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Receives:</span>
                <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{nodeDetails.receives_from}</span>
              </div>
            )}
          </div>
          {nodeDetails.outputs_to && (
            <div className={`flex items-center space-x-1 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
              <ArrowRight className="w-3 h-3" />
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Outputs:</span>
              <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{nodeDetails.outputs_to}</span>
            </div>
          )}

          {/* Agent Input Hint */}
          {hasAgentInput && agentInputHint && (
            <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-cyan-900/30 border border-cyan-700' : 'bg-cyan-50 border border-cyan-200'}`}>
              <div className="flex items-center space-x-2 mb-2">
                <ArrowDownCircle className="w-4 h-4 text-cyan-400" />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>Agent Input Required</span>
              </div>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{agentInputHint.reason}</p>
              {agentInputHint.expected_inputs && agentInputHint.expected_inputs.length > 0 && (
                <div className="space-y-1.5">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected Inputs:</span>
                  {agentInputHint.expected_inputs.map((input, i) => (
                    <div key={i} className={`flex items-center space-x-2 text-xs rounded px-2 py-1.5 ${isDarkMode ? 'bg-gray-900/50 border border-cyan-700/40' : 'bg-gray-50/50 border border-cyan-300/40'}`}>
                      <span className={`font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>{input.name}</span>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>({input.type})</span>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>- {input.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Integration Hint */}
          {hasIntegration && integrationHint && (
            <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-rose-900/30 border border-rose-700' : 'bg-rose-50 border border-rose-200'}`}>
              <div className="flex items-center space-x-2 mb-2">
                <Database className="w-4 h-4 text-rose-400" />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}>Integration Context Needed</span>
                {integrationHint.suggested_source && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-500/20 text-rose-600'}`}>
                    {integrationHint.suggested_source.toUpperCase()}
                  </span>
                )}
              </div>
              <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{integrationHint.reason}</p>
              {integrationHint.query_description && (
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}><span className={`font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Query:</span> {integrationHint.query_description}</p>
              )}
              {integrationHint.uses_from_previous && (
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}><span className={`font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Uses:</span> {integrationHint.uses_from_previous}</p>
              )}
            </div>
          )}

          {/* Agent Output Hint */}
          {hasAgentOutput && agentOutputHint && (
            <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center space-x-2 mb-2">
                <ArrowUpCircle className="w-4 h-4 text-green-400" />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Agent Output</span>
              </div>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{agentOutputHint.reason}</p>
              {agentOutputHint.expected_outputs && agentOutputHint.expected_outputs.length > 0 && (
                <div className="space-y-1.5">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected Outputs:</span>
                  {agentOutputHint.expected_outputs.map((output, i) => (
                    <div key={i} className={`flex items-center space-x-2 text-xs rounded px-2 py-1.5 ${isDarkMode ? 'bg-gray-900/50 border border-green-700/40' : 'bg-gray-50/50 border border-green-300/40'}`}>
                      <span className={`font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{output.name}</span>
                      <span className="text-gray-500">({output.type})</span>
                      <span className="text-gray-600">- {output.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Generated Schemas - shown after agent is created */}
          {hasGeneratedSchemas && (
            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center space-x-2 mb-3">
                <CheckCircle className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Generated Schemas</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderSchema(agentNode.node_input, 'Node Input', {
                  darkBg: 'bg-purple-900/30',
                  darkBorder: 'border border-purple-700',
                  lightBg: 'bg-purple-50',
                  lightBorder: 'border border-purple-200',
                  icon: isDarkMode ? 'text-purple-400' : 'text-purple-700'
                })}
                {renderSchema(agentNode.node_output, 'Node Output', {
                  darkBg: 'bg-amber-900/30',
                  darkBorder: 'border border-amber-700',
                  lightBg: 'bg-amber-50',
                  lightBorder: 'border border-amber-200',
                  icon: isDarkMode ? 'text-amber-400' : 'text-amber-700'
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Accordion 1: Flow Description */}
      <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'border border-[#0077A8]/50' : 'border-0 shadow-md'}`}>
        <button
          onClick={() => setDescriptionExpanded(!descriptionExpanded)}
          className="w-full flex items-center justify-between p-4 transition-all hover:opacity-90"
          style={{
            background: isDarkMode
              ? 'rgba(0, 119, 168, 0.2)'
              : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
          }}
        >
          <h4 className={`font-semibold flex items-center ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`}>
            <FileText className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            Flow Description
          </h4>
          <div className="flex items-center space-x-4">
            <span className={`text-sm ${isDarkMode ? 'text-[#0099CC]' : 'text-white/90'}`}>
              <strong>Cost:</strong> {workflowResult.total_estimated_cost}
            </span>
            <span className={`text-sm ${isDarkMode ? 'text-[#0099CC]' : 'text-white/90'}`}>
              <strong>Time:</strong> {workflowResult.total_estimated_time}
            </span>
            {descriptionExpanded ? (
              <ChevronUp className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            )}
          </div>
        </button>
        {descriptionExpanded && (
          <div className={`p-4 border-t space-y-4 ${isDarkMode ? 'bg-[#0077A8]/10 border-[#0077A8]/30' : 'bg-[#0077A8]/5 border-[#0077A8]/20'}`}>
            {/* Summary */}
            <div>
              <h5 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-[#0099CC]' : 'text-[#0077A8]'}`}>Summary</h5>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{workflowResult.workflow_summary}</p>
            </div>
            {/* Design Reasoning */}
            {workflowResult.decomposition_reasoning && (
              <div>
                <h5 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-[#0099CC]' : 'text-[#0077A8]'}`}>Design Reasoning</h5>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{workflowResult.decomposition_reasoning}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Accordion 2: Workflow (Flow Visualization) */}
      <div className={`rounded-lg overflow-visible ${isDarkMode ? 'border border-[#0077A8]/50' : 'border-0 shadow-md'}`}>
        <button
          onClick={() => setFlowExpanded(!flowExpanded)}
          className="w-full flex items-center justify-between p-4 transition-all hover:opacity-90"
          style={{
            background: isDarkMode
              ? 'rgba(0, 119, 168, 0.2)'
              : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
          }}
        >
          <h4 className={`font-semibold flex items-center ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`}>
            <Workflow className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            Agent Design
          </h4>
          <div className="flex items-center space-x-4">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
              {(workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []).length} nodes
            </span>
            {flowExpanded ? (
              <ChevronUp className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            )}
          </div>
        </button>
        {flowExpanded && (
          <div className={`border-t ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50/50 border-gray-200'}`} style={{ overflow: 'visible' }}>
            {/* Flow Tabs with sliding pill - hide other tabs after agent selection */}
            {workflowResult.flows && workflowResult.flows.length > 1 && !agentId && !isEditingFlow && (
              <div
                ref={flowTabContainerRef}
                className={`relative flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                {/* Animated sliding pill */}
                <div
                  className="absolute top-2 bottom-2 rounded-full transition-all duration-300 ease-out"
                  style={{
                    left: flowPillStyle.left,
                    width: flowPillStyle.width,
                    opacity: flowPillStyle.opacity,
                    background: currentFlowIndex === 0
                      ? isDarkMode
                        ? 'linear-gradient(135deg, rgba(0, 119, 168, 0.5), rgba(0, 153, 204, 0.4))'
                        : 'linear-gradient(135deg, rgba(0, 119, 168, 0.25), rgba(0, 153, 204, 0.2))'
                      : currentFlowIndex === 1
                        ? isDarkMode
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.5), rgba(52, 211, 153, 0.4))'
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(52, 211, 153, 0.2))'
                        : isDarkMode
                          ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.5), rgba(168, 85, 247, 0.4))'
                          : 'linear-gradient(135deg, rgba(147, 51, 234, 0.25), rgba(168, 85, 247, 0.2))',
                    boxShadow: currentFlowIndex === 0
                      ? '0 0 20px rgba(0, 119, 168, 0.3)'
                      : currentFlowIndex === 1
                        ? '0 0 20px rgba(16, 185, 129, 0.3)'
                        : '0 0 20px rgba(147, 51, 234, 0.3)',
                  }}
                />
                {workflowResult.flows.map((flow, index) => (
                  <button
                    key={index}
                    ref={el => flowTabRefs.current[index] = el}
                    onClick={() => {
                      setCurrentFlowIndex(index);
                      setCurrentNodeIndex(0);
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-all relative z-10 ${
                      index === currentFlowIndex
                        ? isDarkMode
                          ? 'text-white'
                          : 'text-gray-900'
                        : isDarkMode
                          ? 'text-gray-400 hover:text-gray-200'
                          : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {/* Selection indicator - tick or cross */}
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                      index === currentFlowIndex
                        ? index === 0
                          ? 'bg-[#0077A8] text-white scale-110'
                          : index === 1
                            ? 'bg-emerald-600 text-white scale-110'
                            : 'bg-purple-600 text-white scale-110'
                        : isDarkMode
                          ? 'bg-gray-600/50 text-gray-400 scale-100'
                          : 'bg-gray-200 text-gray-400 scale-100'
                    }`}>
                      {index === currentFlowIndex
                        ? <Check className="w-3 h-3" strokeWidth={3} />
                        : <X className="w-3 h-3" strokeWidth={2} />
                      }
                    </div>
                    <div className="flex flex-col items-center">
                      <span className={`text-xs uppercase tracking-wide mb-1 transition-all duration-300 ${
                        index === currentFlowIndex
                          ? index === 0
                            ? 'text-[#0099CC] font-semibold'
                            : index === 1
                              ? 'text-emerald-400 font-semibold'
                              : 'text-purple-400 font-semibold'
                          : index === 0
                            ? 'text-[#0099CC]/60'
                            : index === 1
                              ? 'text-emerald-500/60'
                              : 'text-purple-500/60'
                      }`}>
                        {index === 0 ? 'Recommended' : index === 1 ? 'Simple' : 'Thorough'}
                      </span>
                      <span>{flow.label || `Flow ${index + 1}`}</span>
                      <span className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {flow.workflow_steps?.length || 0} steps
                      </span>
                    </div>
                    {/* Active tab bottom indicator */}
                    <div className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-300 ${
                      index === currentFlowIndex
                        ? index === 0 ? 'bg-[#0099CC]' : index === 1 ? 'bg-emerald-500' : 'bg-purple-500'
                        : 'bg-transparent'
                    }`} />
                  </button>
                ))}
              </div>
            )}

            {/* Flow Description / Selected Flow Indicator */}
            {workflowResult.flows && workflowResult.flows.length > 1 && !isEditingFlow && (
              <div className={`px-4 py-2 ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
                {agentId ? (
                  /* Show selected flow badge when agent is created */
                  <div className="flex items-center space-x-2">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      currentFlowIndex === 0
                        ? 'bg-[#0077A8]/20 text-[#0099CC] border border-[#0077A8]/30'
                        : currentFlowIndex === 1
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    }`}>
                      <Check className="w-3 h-3 mr-1.5" />
                      <span className="font-semibold">{workflowResult.flows[currentFlowIndex]?.label || `Flow ${currentFlowIndex + 1}`}</span>
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      • {workflowResult.flows[currentFlowIndex]?.workflow_steps?.length || 0} steps
                    </span>
                  </div>
                ) : (
                  /* Show flow description when no agent yet */
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {workflowResult.flows[currentFlowIndex]?.description}
                  </span>
                )}
              </div>
            )}

            {/* Render the flow visualization - always use sketchy FlowVisualization */}
            <div className={`px-4 pb-8 ${isEditingFlow ? 'pt-4' : 'pt-16'}`} style={{ overflow: 'visible', minHeight: '280px' }}>
              <FlowVisualization
                nodes={(workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []).map((step, idx) => ({
                  nodeId: step.node?.node_id || `node-${idx}`,
                  alias: step.node?.display_name || `Node ${idx + 1}`,
                  description: step.node?.business_function || ''
                }))}
                workflowSteps={workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []}
                isDarkMode={isDarkMode}
                agentDefinition={agentDefinition}
                variableMappings={null}
                currentNodeIndex={currentNodeIndex}
                onNodeClick={(index) => {
                  if (isEditingFlow) {
                    const closing = editingNodeIndex === index;
                    setEditingNodeIndex(closing ? null : index);
                    if (!closing && editedNodes?.[index]) {
                      // Initialize draft from editedNodes when opening a card
                      setDraftNodeValues({
                        alias: editedNodes[index].alias || '',
                        description: editedNodes[index].description || '',
                      });
                    } else if (closing) {
                      setDraftNodeValues(null);
                    }
                  } else {
                    setCurrentNodeIndex(index);
                    setDetailsExpanded(true);
                    setActiveDetailTab('node-details');
                  }
                }}
                isEditing={isEditingFlow}
                editingNodeIndex={editingNodeIndex}
                editedNodes={editedNodes}
                nodeValidations={nodeValidations}
                onNodeEdit={handleNodeEdit}
                onNodeRemove={handleRemoveNode}
                onNodeAdd={handleAddNode}
                onNodeValidate={handleValidateNode}
                onApplySuggestion={handleApplySuggestion}
                draftNodeValues={draftNodeValues}
                onDraftChange={setDraftNodeValues}
                onEditingNodeChange={setEditingNodeIndex}
                isValidatingNode={isValidatingNode}
              />

              {/* Edit mode action bar - below the flow visualization */}
              {isEditingFlow && (
                <div className={`mt-4 flex items-center justify-between px-2 py-3 rounded-lg ${
                  isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {editedNodes?.length || 0} nodes &middot; {Object.values(nodeValidations).filter(v => v?.valid).length} validated
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                        isDarkMode
                          ? 'text-gray-300 hover:bg-gray-700 border border-gray-600'
                          : 'text-gray-600 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFinalizeEdits}
                      disabled={!allEditedNodesValidated || isSelectingFlow}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors bg-[#0077A8] text-white hover:bg-[#006090] ${
                        (!allEditedNodesValidated || isSelectingFlow) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSelectingFlow ? 'Creating...' : 'Finalize & Generate Schemas'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Accordion 3: Details (Tabbed - Node Details, Data, Prompts, I/O) */}
      <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'border border-[#0077A8]/50' : 'border-0 shadow-md'}`}>
        <button
          onClick={() => setDetailsExpanded(!detailsExpanded)}
          className="w-full flex items-center justify-between p-4 transition-all hover:opacity-90"
          style={{
            background: isDarkMode
              ? 'rgba(0, 119, 168, 0.2)'
              : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
          }}
        >
          <h4 className={`font-semibold flex items-center ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`}>
            <FileText className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            Details
          </h4>
          <div className="flex items-center space-x-4">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>
              Node {currentNodeIndex + 1} of {(workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []).length}
            </span>
            {detailsExpanded ? (
              <ChevronUp className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`} />
            )}
          </div>
        </button>
        {detailsExpanded && (
          <div className={`p-4 border-t ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50/50 border-gray-200'}`}>
            {workflowResult.flows && workflowResult.flows.length > 0
              ? renderDetailsTabs(workflowResult.flows[currentFlowIndex]?.workflow_steps || [])
              : renderDetailsTabs(workflowResult.primary_workflow)
            }
          </div>
        )}
      </div>
      {workflowResult.mcp_tools && workflowResult.mcp_tools.length > 0 && (
  <div className="space-y-4">
    <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>MCP Tools Configuration</h4>
    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Zap className="w-4 h-4 text-green-400" />
          <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
            {workflowResult.mcp_tools.length} MCP Tool{workflowResult.mcp_tools.length > 1 ? 's' : ''} Available
          </span>
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(JSON.stringify(workflowResult.mcp_tools, null, 2));
            addMessage('bot', 'MCP tools JSON copied to clipboard!');
          }}
          className={`flex items-center space-x-1 px-2 py-1 rounded ${isDarkMode ? 'text-green-400 hover:text-green-300 hover:bg-green-800/50 dark-tooltip' : 'text-green-400 hover:text-green-700 hover:bg-green-200 light-tooltip'}`}
          data-tooltip="Copy MCP JSON"
        >
          <Copy className="w-3 h-3" />
          <span className="text-xs">Copy JSON</span>
        </button>
      </div>
      <div className={`rounded-md p-3 overflow-x-auto ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <pre className={`text-xs font-mono ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
          {JSON.stringify(workflowResult.mcp_tools, null, 2)}
        </pre>
      </div>
      <div className={`mt-3 text-xs ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
        <p className="font-medium mb-1">These tools will be triggered after the final node completes:</p>
        <ul className="space-y-1 ml-4">
          {workflowResult.mcp_tools.map((tool, idx) => (
            <li key={idx} className="flex items-start space-x-2">
              <span>•</span>
              <span><strong>{tool.name}:</strong> {tool.description}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
)}

      {workflowResult.suggested_tools && workflowResult.suggested_tools.length > 0 && (
        <div className="space-y-4">
          <h4 className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Suggested Tools</h4>
          <div className="grid grid-cols-1 gap-3">
            {workflowResult.suggested_tools.map((tool, index) => (
              <div key={index} className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <div className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{tool.tool}</div>
                <div className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{tool.purpose}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTAs for Workflow/Decompose view */}
      <div className={`pt-6 mt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col space-y-3">

          {/* Edit mode: Finalize + Cancel buttons */}
          {isEditingFlow ? (
            <>
              <button
                onClick={handleFinalizeEdits}
                disabled={!allEditedNodesValidated || isSelectingFlow}
                className={`w-full px-4 py-2 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 flex items-center justify-center space-x-2 ${(!allEditedNodesValidated || isSelectingFlow) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSelectingFlow ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Creating Agent...</span>
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4" />
                    <span>Finalize & Generate Schemas</span>
                  </>
                )}
              </button>
              <button
                onClick={handleCancelEdit}
                className={`w-full px-4 py-2 rounded-md flex items-center justify-center space-x-2 ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                <X className="w-4 h-4" />
                <span>Cancel Editing</span>
              </button>
            </>
          ) : (
            <>
              {/* Primary CTA - Proceed to finalize with selected flow */}
              <button
                onClick={handleProceedToData}
                disabled={isSelectingFlow}
                className={`w-full px-4 py-2 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] focus:outline-none focus:ring-2 focus:ring-[#0077A8] focus:ring-offset-2 flex items-center justify-center space-x-2 ${isSelectingFlow ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSelectingFlow ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Creating Agent...</span>
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4" />
                    <span>
                      {agentId
                        ? (agentDefinition?.status === 'variables_mapped' || agentDefinition?.status === 'VARIABLES_MAPPED')
                          ? 'Generate Prompts'
                          : (agentDefinition?.status === 'prompts_ready' || agentDefinition?.status === 'PROMPTS_READY')
                            ? 'Run Agent'
                            : 'Map Variables'
                        : workflowResult?.flows && workflowResult.flows.length > 1
                          ? `Use ${workflowResult.flows[currentFlowIndex]?.label || 'This'} Flow`
                          : 'Use This Flow'
                      }
                    </span>
                  </>
                )}
              </button>

              {/* Edit This Flow button - only show before agent is created */}
              {!agentId && workflowResult?.flows && (
                <button
                  onClick={handleEditFlow}
                  className={`w-full px-4 py-2 rounded-md flex items-center justify-center space-x-2 ${
                    isDarkMode
                      ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Edit3 className="w-4 h-4" />
                  <span>I want to tweak this</span>
                </button>
              )}

              {/* Auto-pilot: Let Byte handle it */}
              {!agentId && workflowResult?.flows && (
                <button
                  onClick={runAutoPilot}
                  disabled={isSelectingFlow || isAutoPilot}
                  className="byte-magic-btn w-full px-4 py-3 rounded-xl flex items-center justify-center space-x-2 transition-all duration-200 text-base"
                >
                  <Sparkles className="w-5 h-5 relative z-10" />
                  <span className="relative z-10">Let Byte handle it</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowView;
